import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Seo from "@site/components/Seo";
import Layout from "@site/components/layout/Layout";
import { Phone, User, Scale, Calendar, Briefcase, FileText, type LucideIcon } from "lucide-react";
import AboutSection from "@site/components/home/AboutSection";
import FirmDescriptionSection from "@site/components/home/FirmDescriptionSection";
import PracticeAreasSection from "@site/components/home/PracticeAreasSection";
import PracticeAreasGrid from "@site/components/home/PracticeAreasGrid";
import AwardsSection from "@site/components/home/AwardsSection";
import TestimonialsSection from "@site/components/home/TestimonialsSection";
import HomeFeatureBoxesSection from "@site/components/home/HomeFeatureBoxesSection";
import AttorneyInfoSection from "@site/components/home/AttorneyInfoSection";
import HomeFaqSection from "@site/components/home/HomeFaqSection";
import { lazy, Suspense } from "react";
import { useHomeContent } from "@site/hooks/useHomeContent";
import { useGlobalPhone, useSiteSettings } from "@site/contexts/SiteSettingsContext";
import { resolveSeo } from "@site/utils/resolveSeo";
import { getSiteUrlFallback } from "@site/lib/runtime-env";
import { getOptimizedImageUrl } from "@site/lib/imageOptimizer";
import HeroBackgroundImage from "@site/components/home/HeroBackgroundImage";
import DeferredSection from "@site/components/home/DeferredSection";

const ContactUsSection = lazy(
  () => import("@site/components/home/ContactUsSection"),
);

export default function Index() {
  const { pathname } = useLocation();
  const { content, page, isLoading } = useHomeContent(pathname);
  const { phoneDisplay, phoneLabel } = useGlobalPhone();
  const siteSettings = useSiteSettings();
  const siteUrl = siteSettings.settings.siteUrl || getSiteUrlFallback();
  const aboutNavItem = siteSettings.settings.navigationItems.find(
    (item) => item.label?.trim().toLowerCase() === "about us" && item.href,
  );
  const aboutFallbackHref = aboutNavItem?.href?.startsWith("/")
    ? aboutNavItem.href
    : aboutNavItem?.href
      ? `/${aboutNavItem.href}`
      : "/";

  // Centralized SEO resolution
  const seo = resolveSeo(page, siteSettings.settings, pathname, siteUrl);

  const heroContent = content.hero;
  const featureBoxes = content.featureBoxes;
  const heroButtons = heroContent.buttons;

  // Icon map for CMS-driven icons
  const iconMap: Record<string, LucideIcon> = {
    User,
    Phone,
    Scale,
    Calendar,
    Briefcase,
    FileText,
  };

  return (
    <Layout heroBg={(heroContent as any).heroBgImage || undefined}>
      <Seo {...seo} />

      {/* Hero Section */}
      <div className="relative -mt-[144px] pt-[171px] pb-[27px] w-full bg-law-accent">
        {(heroContent as any).heroBgImage && (
          <HeroBackgroundImage src={(heroContent as any).heroBgImage} />
        )}
        <div className="relative z-10 max-w-[2560px] mx-auto w-[95%] flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-[3%]">
          {/* Left Side: Headline and Call Box */}
          <div className="lg:w-[65.667%]">
            <div className="mb-[30px] md:mb-[40px]">
              <div className="relative">
                <p className="font-playfair text-[clamp(2.5rem,7vw,68.8px)] font-light leading-[1.2] text-black text-left">
                  {(() => {
                    const headline = heroContent.headline || '';
                    const highlight = heroContent.highlightedText || '';
                    if (!highlight || !headline.toLowerCase().includes(highlight.toLowerCase())) {
                      return (
                        <>
                          {highlight && <><span className="text-law-accent">{highlight}</span><br /></>}
                          {headline}
                        </>
                      );
                    }
                    const idx = headline.toLowerCase().indexOf(highlight.toLowerCase());
                    const before = headline.slice(0, idx);
                    const match = headline.slice(idx, idx + highlight.length);
                    const after = headline.slice(idx + highlight.length);
                    return (
                      <>
                        {before}<span className="text-law-accent">{match}</span>{after}
                      </>
                    );
                  })()}
                </p>
              </div>
              {heroContent.h1Title && (
                <h1 className="font-outfit text-[18px] md:text-[20px] font-medium tracking-wider uppercase text-black mt-[20px] md:mt-[30px]">
                  {heroContent.h1Title}
                </h1>
              )}
            </div>

            {/* Hero Buttons Row */}
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-[820px]">
              {/* Call Box - always uses global phone */}
              <a
                href={`tel:${phoneDisplay.replace(/[^0-9]/g, "")}`}
                data-dni-phone="primary"
                className="bg-law-accent p-[8px] w-full sm:w-1/2 cursor-pointer transition-all duration-300 hover:bg-law-accent-dark group block"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-white p-[15px] mt-1 flex items-center justify-center group-hover:bg-black transition-colors duration-300">
                    <Phone className="w-8 h-8 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-outfit text-[16px] md:text-[18px] leading-tight text-black pb-[10px] font-normal group-hover:text-white transition-colors duration-300">
                      {phoneLabel}
                    </p>
                    <p className="font-outfit text-[clamp(1.75rem,5vw,40px)] text-black leading-tight group-hover:text-white transition-colors duration-300">
                      {phoneDisplay}
                    </p>
                  </div>
                </div>
              </a>

              {/* Dynamic hero buttons from CMS */}
              {heroButtons.length > 0 ? (
                heroButtons.map((btn, i) => {
                  const IconComponent = btn.icon && iconMap[btn.icon] ? iconMap[btn.icon] : null;

                  return (
                    <Link key={i} to={btn.href || aboutFallbackHref} className="w-full sm:w-1/2">
                      <div className="bg-law-accent p-[8px] h-full cursor-pointer transition-all duration-300 hover:bg-law-accent-dark group">
                        {IconComponent ? (
                          <div className="flex items-center gap-4">
                            <div className="bg-white p-[15px] flex items-center justify-center group-hover:bg-black transition-colors duration-300">
                              <IconComponent className="w-8 h-8 text-black group-hover:text-white transition-colors duration-300" />
                            </div>
                            <span className="font-outfit text-[clamp(1.5rem,4vw,32px)] text-black font-semibold leading-tight tracking-wide group-hover:text-white transition-colors duration-300">
                              {btn.label}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <span className="font-outfit text-[clamp(1.5rem,4vw,32px)] text-black font-light group-hover:text-white transition-colors duration-300">
                              {btn.label}
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })
              ) : (
                <Link to={aboutFallbackHref} className="w-full sm:w-1/2">
                  <div className="bg-law-accent p-[8px] h-full group hover:bg-law-accent-dark transition-colors duration-300">
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-[15px] mt-1 flex items-center justify-center group-hover:bg-black transition-colors duration-300">
                        <User className="w-8 h-8 text-black group-hover:text-white transition-colors duration-300" />
                      </div>
                      <span className="font-outfit text-[clamp(1.5rem,4vw,32px)] text-black font-semibold leading-tight tracking-wide group-hover:text-white transition-colors duration-300">
                        Attorney Profile
                      </span>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Right Side: Attorney Image */}
          <div className="lg:w-[31.3333%] flex items-end justify-center">
            {(heroContent as any).heroImage && (
              <img
                src={getOptimizedImageUrl((heroContent as any).heroImage, {
                  width: 700,
                  quality: 75,
                  resize: "contain",
                })}
                alt="Attorney"
                width={700}
                height={480}
                className="max-w-full w-auto h-auto object-contain max-h-[480px]"
                style={{
                  maskImage: "linear-gradient(to bottom, black 60%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to bottom, black 60%, transparent 100%)",
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <TestimonialsSection content={content.testimonials} />

      {/* Feature Boxes Section */}
      <HomeFeatureBoxesSection content={featureBoxes} />

      {/* About Us Section */}
      <DeferredSection intrinsicBlockSize={760}>
        <AboutSection content={content.about} />
      </DeferredSection>

      {/* Firm Description Section */}
      <DeferredSection intrinsicBlockSize={720}>
        <FirmDescriptionSection content={content.firmDescription} />
      </DeferredSection>

      {/* Practice Areas Section */}
      <DeferredSection intrinsicBlockSize={420}>
        <PracticeAreasSection content={content.practiceAreasIntro} />
      </DeferredSection>

      {/* Practice Areas Grid */}
      <DeferredSection intrinsicBlockSize={900}>
        <PracticeAreasGrid areas={content.practiceAreas} />
      </DeferredSection>

      {/* Awards CTA Section */}
      <DeferredSection intrinsicBlockSize={480}>
        <AwardsSection content={content.awardsCTA} />
      </DeferredSection>

      {/* Attorney Info Section */}
      <DeferredSection intrinsicBlockSize={1100}>
        <AttorneyInfoSection content={content.attorneyInfo} />
      </DeferredSection>

      {/* FAQ Section */}
      <DeferredSection intrinsicBlockSize={700}>
        <HomeFaqSection content={content.faq} />
      </DeferredSection>

      {/* Contact Us Section */}
      <DeferredSection intrinsicBlockSize={850}>
        <Suspense fallback={<div className="min-h-[850px]" />}>
          <ContactUsSection content={content.contact} />
        </Suspense>
      </DeferredSection>
    </Layout>
  );
}
